// WYVIO Motion Design System — Variants Framer Motion
// Variants réutilisables (initial / animate / exit / whileHover / whileTap).
// Tout composant animé doit importer d'ici. Pas de valeurs magiques ailleurs.

import { duration, easing, spring, distance, stagger } from "./tokens";

/* ─────────────────────────── FADE ─────────────────────────── */
export const fade = {
  initial: { opacity: 0 },
  animate: { opacity: 1, transition: { duration: duration.base, ease: easing.premium } },
  exit:    { opacity: 0, transition: { duration: duration.fast, ease: easing.exit } },
};

/* ─────────────────────────── SLIDE ────────────────────────── */
// Slide vertical (par défaut) — apparition d'une carte, section, notification.
export const slide = {
  initial: { opacity: 0, y: distance.sm },
  animate: {
    opacity: 1,
    y: 0,
    transition: { duration: duration.base + 0.04, ease: easing.premium },
  },
  exit: {
    opacity: 0,
    y: -distance.xs,
    transition: { duration: duration.fast, ease: easing.exit },
  },
};

export const slideRight = {
  initial: { opacity: 0, x: -distance.sm },
  animate: { opacity: 1, x: 0, transition: { duration: duration.base, ease: easing.premium } },
  exit:    { opacity: 0, x: -distance.xs, transition: { duration: duration.fast, ease: easing.exit } },
};

/* ─────────────────────────── SCALE ────────────────────────── */
// Apparition douce d'un élément qui "arrive". Toolbars, popovers.
export const scale = {
  initial: { opacity: 0, scale: 0.96 },
  animate: {
    opacity: 1,
    scale: 1,
    transition: { duration: duration.base, ease: easing.premium },
  },
  exit: {
    opacity: 0,
    scale: 0.98,
    transition: { duration: duration.fast, ease: easing.exit },
  },
};

/* ─────────────────────────── CARD HOVER ───────────────────── */
// Élévation subtile — 2 px seulement. Pas de sur-élévation cartoon.
export const cardHover = {
  rest: { y: 0, boxShadow: "0 1px 2px rgba(0,0,0,0.04)" },
  hover: {
    y: -2,
    boxShadow: "0 8px 24px rgba(0,0,0,0.06), 0 1px 2px rgba(0,0,0,0.04)",
    transition: spring.stiff,
  },
  tap: { y: 0, scale: 0.995, transition: spring.stiff },
};

/* ─────────────────────────── BUTTON PRESS ─────────────────── */
// Feedback tap — scale 0.98 puis relâchement spring.
export const buttonPress = {
  rest:  { scale: 1 },
  hover: { scale: 1.01, transition: spring.stiff },
  tap:   { scale: 0.98, transition: spring.stiff },
};

/* ─────────────────────────── DRAWER ───────────────────────── */
// Glisse de la droite (défaut). Backdrop flouté à part.
export const drawerTransition = {
  initial: { x: "100%" },
  animate: { x: 0, transition: { duration: duration.slow, ease: easing.premium } },
  exit:    { x: "100%", transition: { duration: duration.base + 0.04, ease: easing.exit } },
};

export const drawerBackdrop = {
  initial: { opacity: 0 },
  animate: { opacity: 1, transition: { duration: duration.base, ease: easing.premium } },
  exit:    { opacity: 0, transition: { duration: duration.fast, ease: easing.exit } },
};

/* ─────────────────────────── MODAL ────────────────────────── */
export const modalTransition = {
  initial: { opacity: 0, scale: 0.98, y: 8 },
  animate: {
    opacity: 1,
    scale: 1,
    y: 0,
    transition: { duration: duration.base + 0.04, ease: easing.premium },
  },
  exit: {
    opacity: 0,
    scale: 0.98,
    y: 4,
    transition: { duration: duration.fast, ease: easing.exit },
  },
};

/* ─────────────────────────── HERO / SHARED ────────────────── */
// Utilisé avec layoutId="dossier-{id}" — la carte s'agrandit en fiche.
// La transition est portée par Framer via <motion.div layoutId>.
// Ce preset est le paramètre `transition` à passer au motion component.
export const heroTransition = {
  layout: { duration: duration.hero, ease: easing.premium },
  ...spring.soft,
};

// Transition de layout générique (reflow doux d'une grille par ex.)
export const sharedTransition = {
  duration: duration.base + 0.04,
  ease: easing.premium,
};

/* ─────────────────────────── STAGGER ──────────────────────── */
// Container qui décale ses enfants. Pour listes / grilles.
export const staggerContainer = {
  initial: {},
  animate: {
    transition: {
      staggerChildren: stagger.base,
      delayChildren: 0.02,
    },
  },
  exit: {
    transition: {
      staggerChildren: stagger.tight,
      staggerDirection: -1,
    },
  },
};

// Child utilisable dans un staggerContainer — hérite du timing.
export const staggerItem = slide;

/* ─────────────────────────── PAGE TRANSITION ──────────────── */
// Transition entre routes — reste très discrète.
export const pageTransition = {
  initial: { opacity: 0, y: 6 },
  animate: {
    opacity: 1,
    y: 0,
    transition: { duration: duration.base + 0.04, ease: easing.premium },
  },
  exit: {
    opacity: 0,
    transition: { duration: duration.fast, ease: easing.exit },
  },
};
